import { createState } from "@tecnobit-srl/state";
import axios from "axios";
import { camelCase, findKey, isArray, isObject, isString, join, map, mapKeys, mapValues } from "lodash-es";
import { useCallback, useEffect, useMemo } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
//#region src/sanitize-data.tsx
var sanitizeData = (data) => {
	if (isArray(data)) return map(data, (d) => sanitizeData(d));
	if (isObject(data)) return mapValues(mapKeys(data, (_, s) => isString(s) && s.startsWith("x_") ? camelCase(s.substring(2)) : s), (d) => sanitizeData(d));
	if (isString(data)) return data.startsWith("x_") ? camelCase(data.substring(2)) : data;
	return data;
};
//#endregion
//#region src/page-merge.ts
var mergePageWithRetainedProps = (currentPage, incomingPage, retainedProps) => {
	if (!currentPage) return incomingPage;
	const retained = new Set(retainedProps);
	return {
		...Object.fromEntries(Object.entries(currentPage).filter(([key]) => retained.has(key))),
		...incomingPage
	};
};
//#endregion
//#region src/route-context-headers.ts
var ROUTE_CONTEXT_HEADER = "X-Tecnobit-Route-Context";
var mergeRouteContextHeaders = (destination, routeContext, headers, location) => {
	const mergedHeaders = new Headers(headers);
	mergedHeaders.delete(ROUTE_CONTEXT_HEADER);
	if (!routeContext || !location) return mergedHeaders;
	try {
		const destinationUrl = new URL(destination, location.href);
		if (["http:", "https:"].includes(destinationUrl.protocol) && destinationUrl.origin === location.origin) mergedHeaders.set(ROUTE_CONTEXT_HEADER, routeContext);
	} catch {}
	return mergedHeaders;
};
//#endregion
//#region src/browser-url.ts
/**
* Return the current client-side URL without its origin or hash.
*
* The route payload may describe a canonical base path while the browser carries
* dynamic filter and pagination segments, so refreshes must use this address.
*/
var getBrowserUrl = ({ pathname, search }) => `${pathname}${search}`;
//#endregion
//#region src/anchor-scroll.ts
var noop = () => {};
var activeCleanup = null;
var lastHandledKey = null;
var getLastHandledAnchorKey = () => lastHandledKey;
var markAnchorKeyHandled = (key) => {
	lastHandledKey = key;
};
/**
* Normalize a `#fragment` (or bare fragment) to its element id.
* Returns null for empty fragments.
*/
var readHashId = (hash) => {
	const raw = hash.startsWith("#") ? hash.slice(1) : hash;
	if (!raw) return null;
	try {
		const decoded = decodeURIComponent(raw).trim();
		return decoded ? decoded : null;
	} catch {
		const trimmed = raw.trim();
		return trimmed ? trimmed : null;
	}
};
/**
* Split a navigation url into the fetchable path and its anchor, keeping the
* raw fragment verbatim so history round-trips byte-identical urls.
*/
var splitAnchor = (url) => {
	const hashIndex = url.indexOf("#");
	if (hashIndex === -1) return {
		path: url,
		anchorId: null,
		hash: ""
	};
	const hash = url.slice(hashIndex);
	return {
		path: url.slice(0, hashIndex),
		anchorId: readHashId(hash),
		hash
	};
};
var anchorTopOf = (element) => element.getBoundingClientRect().top + window.scrollY;
var SCROLL_INTERRUPT_KEYS = /* @__PURE__ */ new Set([
	"ArrowDown",
	"ArrowUp",
	"ArrowLeft",
	"ArrowRight",
	"PageDown",
	"PageUp",
	"Home",
	"End",
	" "
]);
/**
* Scroll to an anchor and re-anchor while the layout shifts underneath it.
*
* The first jump uses `behavior` (`'smooth'` for in-page clicks, `'auto'`
* for direct loads and history traversals); every correction uses
* `correctionBehavior` so queued smooth animations never fight the layout.
* Tracking ends on settle, on `timeoutMs`, or the moment the visitor
* scrolls themselves — whichever comes first. Starting a new stable scroll
* cancels the previous one. Returns a cleanup that stops tracking early.
*/
var stableScrollToAnchorId = (id, options) => {
	if (typeof window === "undefined" || typeof document === "undefined" || !id) return noop;
	activeCleanup?.();
	activeCleanup = null;
	const { behavior = "auto", correctionBehavior = "auto", timeoutMs = 5e3, settleMs = 800, tolerancePx = 2 } = options ?? {};
	let disposed = false;
	let userInterrupted = false;
	let raf = 0;
	let lastAppliedTop = null;
	let lastChangeAt = Date.now();
	let observer = null;
	let timeout = null;
	const currentTop = () => {
		const element = document.getElementById(id);
		if (!element) return null;
		const top = anchorTopOf(element);
		return Number.isNaN(top) ? null : top;
	};
	/** Scroll when the target moved beyond tolerance. True when on target. */
	const apply = (scrollBehavior) => {
		const top = currentTop();
		if (top === null) return false;
		if (lastAppliedTop !== null && Math.abs(top - lastAppliedTop) <= tolerancePx) return true;
		window.scrollTo({
			top,
			behavior: scrollBehavior
		});
		lastAppliedTop = top;
		lastChangeAt = Date.now();
		return true;
	};
	function dispose() {
		if (disposed) return;
		disposed = true;
		cancelAnimationFrame(raf);
		if (timeout !== null) {
			clearTimeout(timeout);
			timeout = null;
		}
		observer?.disconnect();
		observer = null;
		window.removeEventListener("wheel", interrupt);
		window.removeEventListener("touchmove", interrupt);
		window.removeEventListener("pointerdown", interrupt);
		window.removeEventListener("keydown", interruptOnScrollKey);
		window.removeEventListener("load", resync);
		if (activeCleanup === cleanup) activeCleanup = null;
	}
	function interrupt() {
		if (disposed || userInterrupted) return;
		userInterrupted = true;
		dispose();
	}
	function interruptOnScrollKey(event) {
		if (SCROLL_INTERRUPT_KEYS.has(event.key)) interrupt();
	}
	function resync() {
		if (!disposed && !userInterrupted) apply(correctionBehavior);
	}
	const cleanup = () => {
		dispose();
	};
	window.addEventListener("wheel", interrupt, { passive: true });
	window.addEventListener("touchmove", interrupt, { passive: true });
	window.addEventListener("pointerdown", interrupt, { passive: true });
	window.addEventListener("keydown", interruptOnScrollKey);
	window.addEventListener("load", resync);
	try {
		document.fonts.ready.then(() => {
			resync();
		}).catch(() => {});
	} catch {}
	const tick = () => {
		if (disposed) return;
		const top = currentTop();
		if (top !== null && !userInterrupted) {
			if (lastAppliedTop === null) apply(behavior);
			else if (Math.abs(top - lastAppliedTop) > tolerancePx) apply(correctionBehavior);
			else if (Date.now() - lastChangeAt >= settleMs) {
				dispose();
				return;
			}
		}
		raf = requestAnimationFrame(tick);
	};
	apply(behavior);
	raf = requestAnimationFrame(tick);
	if (typeof ResizeObserver !== "undefined") {
		observer = new ResizeObserver(() => {
			if (disposed || userInterrupted) return;
			const top = currentTop();
			if (top === null) return;
			if (lastAppliedTop === null || Math.abs(top - lastAppliedTop) > tolerancePx) apply(correctionBehavior);
		});
		observer.observe(document.documentElement);
		observer.observe(document.body);
	}
	timeout = setTimeout(dispose, timeoutMs);
	activeCleanup = cleanup;
	return cleanup;
};
//#endregion
//#region src/index.tsx
/**
* Set by the server on a response that is a served error page, carrying the status the
* failure produced. Error pages come back as 200 for `X-Inertia` requests — that is how a
* full navigation swaps them in — so the status alone cannot be used to recognize them.
*
* Lower-case because that is how axios normalizes response header names.
*/
var ERROR_PAGE_HEADER = "x-tecnobit-error-page";
/**
* Challenge handoff for tripped anonymous buckets: the backend answers 409
* with the signed `/challenge/cf` URL in this header (same family as the
* error-page header). The router honours it with a full browser navigation.
*
* Lower-case because that is how axios normalizes response header names.
*/
var CHALLENGE_HANDOFF_HEADER = "x-tecnobit-challenge";
var CHALLENGE_HANDOFF_STATUS = 409;
/** Opportunistic reCAPTCHA v3 token header: best-effort, never blocking. */
var RECAPTCHA_TOKEN_HEADER = "X-Tecnobit-Recaptcha-Token";
var readErrorPageStatus = (headers) => {
	if (!headers || typeof headers !== "object") return null;
	const raw = headers[ERROR_PAGE_HEADER];
	if (raw === void 0 || raw === null) return null;
	const status = Number(raw);
	return Number.isFinite(status) ? status : 0;
};
var readChallengeHandoff = (headers) => {
	if (!headers || typeof headers !== "object") return null;
	const raw = headers[CHALLENGE_HANDOFF_HEADER];
	if (typeof raw !== "string" || raw === "") return null;
	return raw.startsWith("/challenge/cf") ? raw : null;
};
/**
* Opportunistic reCAPTCHA v3 token: resolves with the token when
* grecaptcha.execute() succeeds in time, with null otherwise. Never rejects,
* never blocks navigation beyond a short budget.
*/
var getRecaptchaToken = async (action = "navigate", budgetMs = 800) => {
	try {
		const w = window;
		const siteKey = w.__recaptchaSiteKey;
		const execute = w.grecaptcha?.execute;
		if (!siteKey || typeof execute !== "function") return null;
		const timeout = new Promise((resolve) => {
			window.setTimeout(() => {
				resolve(null);
			}, budgetMs);
		});
		const token = await Promise.race([execute.call(w.grecaptcha, siteKey, { action }), timeout]);
		return typeof token === "string" && token !== "" ? token : null;
	} catch {
		return null;
	}
};
/**
* The url the response was actually produced for.
*
* The payload reports the path of the request that produced it, so after a server-side
* redirect it is the destination rather than what we asked for; recording the requested url
* would leave the pre-redirect address in the bar showing the destination's content. The
* query string is not part of that path, so it comes back from the request — minus the flag
* the bridge appends for itself, which has no business in history.
*/
var servedUrlOf = (requested, servedPath) => {
	const queryStart = requested.indexOf("?");
	const requestedPath = queryStart === -1 ? requested : requested.slice(0, queryStart);
	const params = new URLSearchParams(queryStart === -1 ? "" : requested.slice(queryStart + 1));
	params.delete("inertia");
	const query = params.toString();
	const path = (servedPath ?? requestedPath).split("?")[0];
	return query ? `${path}?${query}` : path;
};
var { ContextProvider, useStore } = createState("Router", (set, get) => ({
	loadingOverlay: false,
	eventListeners: {
		navigate: /* @__PURE__ */ new Set(),
		pop: /* @__PURE__ */ new Set()
	},
	currentPage: null,
	hasCompleteBaseline: false,
	navigationSequence: 0,
	setCurrentPage: (page) => {
		set((state) => ({
			...state,
			currentPage: page
		}));
	},
	on: (type, listener) => {
		set((state) => {
			state.eventListeners[type].add(listener);
			return { ...state };
		});
		return () => {
			set((state) => {
				state.eventListeners[type].delete(listener);
				return { ...state };
			});
		};
	},
	navigateTo: async (url, only, replace, silent) => {
		const navigationSequence = get().navigationSequence + (silent ? 0 : 1);
		if (!silent) set((state) => ({
			...state,
			navigationSequence
		}));
		if (!silent) {
			set((state) => ({
				...state,
				loadingOverlay: true
			}));
			document.body.classList.toggle("loading", true);
		}
		try {
			const effectiveOnly = only;
			let except;
			if (effectiveOnly === void 0) {
				const heldPage = get().currentPage;
				const retained = heldPage?.sharedProps?.retained ?? {};
				if (heldPage && get().hasCompleteBaseline && Object.keys(retained).length > 0) except = Object.keys(retained);
			}
			const { path: fetchUrl, hash: urlHash } = splitAnchor(url);
			const recaptchaToken = await getRecaptchaToken();
			const response = await axios.get(fetchUrl.includes("?") ? `${fetchUrl}&inertia` : `${fetchUrl}?inertia`, { headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"Cache-Control": "no-cache",
				Pragma: "no-cache",
				Expires: "0",
				"X-Requested-With": "XMLHttpRequest",
				"X-Inertia": "true",
				...effectiveOnly ? { "X-Inertia-Partial-Data": join(effectiveOnly, ",") } : {},
				...except ? { "X-Inertia-Partial-Except": join(except, ",") } : {},
				...recaptchaToken ? { [RECAPTCHA_TOKEN_HEADER]: recaptchaToken } : {}
			} });
			const earlyHandoff = readChallengeHandoff(response.headers);
			if (earlyHandoff !== null) {
				if (!silent) {
					document.body.classList.toggle("loading", false);
					set((state) => ({
						...state,
						loadingOverlay: false
					}));
					window.location.href = earlyHandoff;
				}
				return {
					type: "external",
					url: earlyHandoff
				};
			}
			const { data } = response;
			if (!data.props) {
				if (!silent) {
					document.body.classList.toggle("loading", false);
					set((state) => ({
						...state,
						loadingOverlay: false
					}));
				}
				throw new Error(`Invalid response from backend for page ${url}`);
			}
			if (navigationSequence !== get().navigationSequence || silent && getBrowserUrl(window.location) !== url) return {
				type: "failed",
				url
			};
			const isFullPage = (!effectiveOnly || effectiveOnly.length === 0) && (!except || except.length === 0);
			const errorPageStatus = readErrorPageStatus(response.headers);
			if (errorPageStatus !== null && !isFullPage) {
				if (!silent) {
					document.body.classList.toggle("loading", false);
					set((state) => ({
						...state,
						loadingOverlay: false
					}));
					window.location.href = url;
				}
				return {
					type: "error-page",
					url,
					status: errorPageStatus
				};
			}
			const sanitizedData = sanitizeData(data.props);
			if (!replace && !urlHash) window.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth"
			});
			let currentPage;
			if (isFullPage) currentPage = sanitizedData;
			else if (except) currentPage = mergePageWithRetainedProps(get().currentPage, sanitizedData, except);
			else currentPage = {
				...get().currentPage ?? {},
				...sanitizedData
			};
			set((state) => ({
				...state,
				currentPage,
				hasCompleteBaseline: state.hasCompleteBaseline || isFullPage
			}));
			const servedUrl = `${servedUrlOf(fetchUrl, data.url)}${urlHash}`;
			if (replace === true) window.history.replaceState(currentPage, "", servedUrl);
			else window.history.pushState(currentPage, "", servedUrl);
			for (const listener of get().eventListeners.navigate) listener(servedUrl, currentPage, effectiveOnly);
			if (!silent) {
				document.body.classList.toggle("loading", false);
				set((state) => ({
					...state,
					loadingOverlay: false
				}));
			}
			return errorPageStatus !== null ? {
				type: "error-page",
				url: servedUrl,
				status: errorPageStatus
			} : {
				type: "navigated",
				url: servedUrl
			};
		} catch (error) {
			const axiosError = error;
			const handoff = axiosError.response?.status === 409 ? readChallengeHandoff(axiosError.response.headers) : null;
			if (handoff !== null) {
				if (!silent) {
					document.body.classList.toggle("loading", false);
					set((state) => ({
						...state,
						loadingOverlay: false
					}));
					window.location.href = handoff;
				}
				return {
					type: "external",
					url: handoff
				};
			}
			if (silent) throw error;
			return {
				type: "failed",
				url,
				error
			};
		}
	},
	reload: async (only, silent) => {
		const url = typeof window !== "undefined" ? getBrowserUrl(window.location) : get().currentPage?.route.fullUrl ?? null;
		if (!url) return {
			type: "failed",
			url: ""
		};
		return get().navigateTo(url, only, true, silent);
	},
	ssrRefresh: async () => {
		const ssrPage = get().currentPage;
		if (!ssrPage) return;
		const navigationSequence = get().navigationSequence;
		const url = getBrowserUrl(window.location);
		try {
			const res = await axios.get(url.includes("?") ? `${url}&inertia` : `${url}?inertia`, { headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"Cache-Control": "no-cache",
				Pragma: "no-cache",
				Expires: "0",
				"X-Requested-With": "XMLHttpRequest",
				"X-Inertia": "true",
				"X-Inertia-Diff": ssrPage.hash
			} });
			if (res.status !== 200) return;
			const { data } = res;
			if (!data.props) return;
			if (navigationSequence !== get().navigationSequence || getBrowserUrl(window.location) !== url) return;
			const updatedPage = sanitizeData(data.props);
			set((state) => ({
				...state,
				currentPage: updatedPage
			}));
			window.history.replaceState(updatedPage, "", `${url}${window.location.hash}`);
			for (const listener of get().eventListeners.navigate) listener(url, updatedPage);
		} catch (error) {}
	}
}));
function InnerElement({ children, loadingElement, initialPage }) {
	const setCurrentPage = useStore((s) => s.setCurrentPage);
	const showLoadingOverlay = useStore((s) => s.loadingOverlay);
	const eventListeners = useStore((s) => s.eventListeners);
	const currentPage = useStore((s) => s.currentPage);
	useEffect(() => {
		window.history.replaceState(initialPage, "");
	}, [initialPage]);
	useEffect(() => {
		try {
			if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
		} catch {}
	}, []);
	useEffect(() => {
		const { hash } = window.location;
		const key = `${window.location.pathname}${window.location.search}${hash}`;
		const id = readHashId(hash);
		if (!id) {
			markAnchorKeyHandled(key);
			return;
		}
		if (getLastHandledAnchorKey() === key) return;
		markAnchorKeyHandled(key);
		stableScrollToAnchorId(id, { behavior: "auto" });
	}, [currentPage]);
	useEffect(() => {
		window.onpopstate = (event) => {
			if (event.state) {
				if (!window.location.hash) window.scrollTo({
					top: 0,
					left: 0,
					behavior: "smooth"
				});
				setCurrentPage(event.state);
				for (const listener of eventListeners.pop) listener(window.location.pathname + window.location.search + window.location.hash, event.state);
			} else return false;
		};
		return () => {
			window.onpopstate = null;
		};
	}, [
		initialPage,
		setCurrentPage,
		eventListeners
	]);
	return /* @__PURE__ */ jsxs(Fragment, { children: [children, showLoadingOverlay ? loadingElement : null] });
}
var usePage = () => {
	return useStore((s) => s.currentPage);
};
var useRouteContextHeaders = () => {
	const routeContext = useStore((s) => s.currentPage?.routeContext);
	return useCallback((destination, headers) => mergeRouteContextHeaders(destination, routeContext, headers, typeof window === "undefined" ? void 0 : window.location), [routeContext]);
};
var useNavigateTo = () => {
	const { ziggy } = usePage();
	const navigateTo = useStore((s) => s.navigateTo);
	return useCallback(async (url, only, replace, silent) => {
		if (isExternalUrl(ziggy, url)) {
			window.location.href = url;
			return {
				type: "external",
				url
			};
		}
		if (url.startsWith("#")) {
			const id = readHashId(url);
			if (id && document.getElementById(id)) {
				window.history.replaceState(history.state, "", url);
				markAnchorKeyHandled(`${window.location.pathname}${window.location.search}${window.location.hash}`);
				stableScrollToAnchorId(id, { behavior: "smooth" });
				return {
					type: "anchor",
					url
				};
			}
			window.location.href = url;
			return {
				type: "external",
				url
			};
		}
		return navigateTo(url, only, replace, silent);
	}, [navigateTo, ziggy]);
};
var useReload = () => {
	return useStore((s) => s.reload);
};
/**
* Replace the current page object. Meant for endpoint-scoped refreshes that merge
* refreshed slices into the existing props, keeping `usePage()` the single source
* of truth for the components that render them.
*/
var useSetCurrentPage = () => {
	return useStore((s) => s.setCurrentPage);
};
var useSsrReload = () => {
	return useStore((s) => s.ssrRefresh);
};
var useAddRouterEventListener = () => {
	return useStore((s) => s.on);
};
function RouterProvider({ children, loadingElement, currentPage }) {
	const initialPage = useMemo(() => sanitizeData(currentPage), [currentPage]);
	return /* @__PURE__ */ jsx(ContextProvider, {
		currentPage: initialPage,
		hasCompleteBaseline: Boolean(initialPage),
		children: /* @__PURE__ */ jsx(InnerElement, {
			initialPage,
			loadingElement,
			children
		})
	});
}
var isMatchingZiggyRoute = (url, route) => {
	if (url === "buy") return false;
	if (route.uri === url) return true;
	if (/\{[^}]+}/.exec(route.uri)) {
		const regex = new RegExp(`${route.uri.replace(/\{[^}]+}/g, "[^/]+").replace(/\/\*$/g, "/.*")}$`);
		return Boolean(regex.exec(url));
	}
	if (/\/\*$/.exec(route.uri)) {
		const regex = new RegExp(`${route.uri.replace(/\/\*$/g, "/.*")}$`);
		return Boolean(regex.exec(url));
	}
	return false;
};
var isExternalUrl = (ziggy, href) => {
	if (!href || href.startsWith("#") || href.startsWith("?") || href === "/" || href === "") return false;
	const baseUrl = window.location.origin;
	let hrefSanitized = href;
	if (hrefSanitized.startsWith("http")) {
		if (!hrefSanitized.startsWith(baseUrl)) return true;
		hrefSanitized = hrefSanitized.substring(baseUrl.length);
	}
	try {
		hrefSanitized = new URL(`${baseUrl}/${hrefSanitized.replace(/^\/+/, "")}`).pathname.replace(/(?:^\/+)|(?:\/+$)/g, "");
	} catch {
		return true;
	}
	return !findKey(ziggy.routes, (route) => route && isMatchingZiggyRoute(hrefSanitized, route))?.startsWith("cms.");
};
//#endregion
export { CHALLENGE_HANDOFF_HEADER, CHALLENGE_HANDOFF_STATUS, ERROR_PAGE_HEADER, RECAPTCHA_TOKEN_HEADER, ROUTE_CONTEXT_HEADER, RouterProvider, getRecaptchaToken, isExternalUrl, mergeRouteContextHeaders, readChallengeHandoff, sanitizeData, useAddRouterEventListener, useNavigateTo, usePage, useReload, useRouteContextHeaders, useSetCurrentPage, useSsrReload };
